import { baseApi } from "../baseApi";
import { pick } from "../util";
import type { Asset, RegisterAssetInput } from "../types";

const ASSET_FIELDS = `
  id
  key
  url
  fileName
  mimeType
  fileSize
  uploadedAt
  uploadedBy
  usedBy {
    entityType
    entityId
    name
  }
  createdAt
`;

export const assetApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAssets: builder.query<Asset[], string | void>({
      query: (organizationId) => ({
        body: {
          query: `
            query Assets($organizationId: String) {
              assets(organizationId: $organizationId) {
                ${ASSET_FIELDS}
              }
            }
          `,
          variables: { organizationId: organizationId ?? null },
        },
      }),

      transformResponse: (res: unknown) => pick<Asset[]>(res, "assets"),

      providesTags: (result) =>
        result
          ? [
              ...result.map((a) => ({ type: "Asset" as const, id: a.id })),
              { type: "Asset" as const, id: "LIST" },
            ]
          : [{ type: "Asset" as const, id: "LIST" }],
    }),

    registerAsset: builder.mutation<Asset, RegisterAssetInput>({
      query: (input) => ({
        body: {
          query: `
            mutation RegisterAsset($key: String!, $fileName: String!, $mimeType: String!, $fileSize: Int!) {
              registerAsset(key: $key, fileName: $fileName, mimeType: $mimeType, fileSize: $fileSize) {
                ${ASSET_FIELDS}
              }
            }
          `,
          variables: input,
        },
      }),

      transformResponse: (res: unknown) => pick<Asset>(res, "registerAsset"),

      invalidatesTags: [{ type: "Asset", id: "LIST" }],
    }),

    deleteAsset: builder.mutation<boolean, string>({
      query: (id) => ({
        body: {
          query: `
            mutation DeleteAsset($id: String!) {
              deleteAsset(id: $id)
            }
          `,
          variables: { id },
        },
      }),

      transformResponse: (res: unknown) => pick<boolean>(res, "deleteAsset"),

      invalidatesTags: (_result, _err, id) => [
        { type: "Asset", id },
        { type: "Asset", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetAssetsQuery,
  useRegisterAssetMutation,
  useDeleteAssetMutation,
} = assetApi;
